<script lang="ts" setup>
import { computed } from 'vue';
import { ref } from 'vue';
import { VTextField } from 'vuetify/components/VTextField';

type Props = {
  /**
   * The initial value of the text field.
   */
  modelValue?: string;
  loading?: boolean;
  /**
   * Show confirm buttons even if value is not changed.
   * Overrides internal show confirm logic.
   */
  showConfirm?: boolean;
  /**
   *
   * @param {string} changed value
   * @returns {boolean | Promise<boolean>} answer if confirm should close
   */
  onApprove?: (s: string) => boolean | Promise<boolean>;
  /**
   *
   * @param {string} changed value
   * @returns {boolean | Promise<boolean>} answer if confirm should close
   */
  onReject?: (s: string) => boolean | Promise<boolean>;

  onUpdate?: (s: string) => void;

  prependInnerIcon?: any;
  type?: 'text' | 'password';
  showPasswordLabel?: string;
  hidePasswordLabel?: string;
};

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  modelValue: '',
  onApprove: () => true,
  onReject: () => true,
  type: 'text',
});
const model = defineModel<string>('modelValue');
const field = ref<VTextField>();

const showPassword = ref(false);
const isPasswordField = computed(() => props.type === 'password');

const fieldType = computed(() => {
  if (!isPasswordField.value) return props.type;
  return showPassword.value ? 'text' : 'password';
});
const toggleVisibility = () => {
  if (props.loading) return;
  showPassword.value = !showPassword.value;
};

type Slot = {
  message: { message: string };
};
const slot = defineSlots<Slot>();

const showConfirmButtons = ref(false);
const onChange = (val: string) => {
  const normalizedVal = val || undefined;
  const normalizedModel = props.modelValue || undefined;
  showConfirmButtons.value = normalizedVal !== normalizedModel;
  props.onUpdate?.(val);
};

const approveClicked = async (s?: string) => {
  if (s === undefined) {
    return;
  }

  if (typeof props.onApprove === 'function') {
    const shouldCloseConfirm = await props.onApprove(s);
    showConfirmButtons.value = !shouldCloseConfirm;
    return;
  }
  showConfirmButtons.value = false;
};

const rejectClicked = (s?: string) => {
  if (s === undefined) {
    return;
  }

  showConfirmButtons.value = false;
  model.value = props.modelValue;
  if (typeof props.onReject === 'function') {
    props.onReject(s);
  }
  onChange(model.value);
  field.value?.resetValidation();
};

const disableConfirm = computed(() => !field.value?.isValid || props.loading);
const disableClear = computed(() => props.loading);
</script>
<template>
  <v-text-field
    ref="field"
    variant="outlined"
    color="primary"
    :single-line="false"
    hide-details="auto"
    v-model="model"
    density="default"
    :disabled="loading"
    :type="fieldType"
    @update:modelValue="onChange"
  >
    <template v-if="slot.message" #message="slotProps">
      <slot name="message" v-bind="slotProps" />
    </template>

    <template v-slot:prepend-inner>
      <template v-if="typeof prependInnerIcon === 'string' && prependInnerIcon.startsWith('data:')">
        <v-img v-bind="props" :src="prependInnerIcon" width="24" height="24"></v-img>
      </template>
      <template v-else>
        <v-icon v-bind="props" :icon="prependInnerIcon"></v-icon>
      </template>
    </template>

    <template v-slot:append-inner>
      <v-btn
        v-if="isPasswordField"
        class="mx-1"
        size="40"
        variant="text"
        :disabled="loading"
        @click="toggleVisibility"
        :aria-label="showPassword ? hidePasswordLabel : showPasswordLabel"
        :title="showPassword ? hidePasswordLabel : showPasswordLabel"
      >
        <v-icon :icon="showPassword ? 'mdi-eye-off' : 'mdi-eye'" />
      </v-btn>

      <v-btn
        v-if="showConfirmButtons || showConfirm"
        :disabled="disableConfirm"
        class="mx-1"
        size="40"
        variant="flat"
        color="primary"
        @click="() => approveClicked(model)"
      >
        <v-icon icon="mdi-check" />
      </v-btn>

      <v-btn
        v-if="showConfirmButtons || showConfirm"
        :disabled="disableClear"
        class="mx-1"
        size="40"
        variant="outlined"
        @click="() => rejectClicked(model)"
      >
        <v-icon icon="mdi-window-close" />
      </v-btn>

      <div class="d-flex" v-if="loading">
        <v-progress-circular indeterminate></v-progress-circular>
      </div>
    </template>
  </v-text-field>
</template>
